<template>
  <v-form>
    <v-autocomplete
      ref="contentTypesSelect"
      v-model="content_type"
      :items="contentTypesToShow"
      item-text="name"
      item-value="wtype"
      :label="getLabel('content_types')"
      chips
      clearable
      :no-data-text="getLabel('table_no_data')"
      :error="content_type === null && errors.length > 0"
      @change="onChangeContentType"
    />
    <v-autocomplete
      ref="contentSelect"
      v-model="selectedLimitedContent"
      :prepend-inner-icon="'mdi-magnify'"
      :loading="loadingContents"
      :search-input.sync="search"
      :items="contents"
      return-object
      :label="getLabel('values')"
      persistent-hint
      clearable
      multiple
      hide-no-data
      no-filter
      :disabled="content_type === null"
      :error="isError"
      class="pb-3"
      @change="onChange"
      @focus="autocompleteWasFocus"
    >
      <template #item="{ item }">
        <v-list-item-avatar
          color="grey"
          class="text-h5 font-weight-light white--text"
        >
          {{ getContentTypeLabel(item.wtype)[0] }}
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-text="item.wtitle" />
          <v-list-item-subtitle>{{ getSubTitle(item, false) }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon v-if="limited_contentsInfo.filter(c => c.uuid === item.uuid).length > 0 " color="green">mdi-check-circle</v-icon>
        </v-list-item-action>
      </template>
    </v-autocomplete>

    <v-list
      v-if="limited_contentsInfo.length > 0"
      dense
      class="mb-4"
    >
      <v-subheader v-if="!preContentsLoading" class="text-body-1">{{ getLabel("limited_contents") }}</v-subheader>
      <v-list-item v-for="(item) in limited_contentsInfo" :key="item.uuid" active>
        <v-chip large class="my-2" color="secondary">
          <v-list-item-content class="d-inline">
            <v-list-item-title class="text-body-1 black--text">{{ item.wtitle }} <v-chip small>{{ getWorkspaceLabel(item.workspace) }}</v-chip></v-list-item-title>
            <v-list-item-subtitle>{{ getSubTitle(item, true) }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click="removeLimitedContent(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-chip>
      </v-list-item>
    </v-list>
    <v-btn v-if="!editMode" outlined @click="previousStep">
      <v-icon class="mr-2">mdi-arrow-left</v-icon>
      {{ getLabel("back") }}
    </v-btn>
    <v-btn class="ml-2" color="success" outlined @click="nextStep">
      {{ getLabel("continue") }}
      <v-icon class="ml-2">mdi-arrow-right</v-icon>
    </v-btn>
  </v-form>
</template>

<script>
import { ref, reactive, computed } from '@vue/composition-api';
import { useQuery } from '@vue/apollo-composable';
import { useMainStore } from '../../store/index';

import { getGraphqlSchema } from '@/graphql/index';
import { getLabel, getWorkspaceLabel, getContentTypeLabel } from '@/utils/index';

export default {
  props: {
    restriction: {
      type: Object,
      required: true
    },
    stepper: {
      type: Number,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    }
  },
  setup (props, { emit, refs }) {
    const store = useMainStore();

    const search = ref('');

    const errors = ref([]);

    const contentTypesToShow = computed(() => store.contentTypes.filter(c => c.project === props.restriction.project).sort((a, b) => a.name.localeCompare(b.name)));

    const content_type = ref(props.restriction.settings.content_type || null);

    const selectedLimitedContent = ref([]);

    const limited_contentsInfo = ref([]);

    const isError = computed(() => limited_contentsInfo.value.length === 0 && errors.value.length > 0);

    const settings = reactive({
      content_type: props.restriction.settings.content_type,
      limited_contents: props.restriction.settings.limited_contents || []
    });

    const triggerSearch = ref(false);

    // Search Request
    const resultCount = ref(0);

    const client = computed(() => {
      if (props.restriction.project === 'alpha') {
        return 'alphaClient';
      }
      return 'default';
    });

    const enableQuery = computed(() => {
      if (props.stepper === 3 && triggerSearch.value && content_type.value) {
        return true;
      }
      return false;
    });

    const facetFilters = computed(() => {
      const arrayWorkspaces = [];

      if (!props.restriction.workspaces.includes('*')) {
        props.restriction.workspaces.forEach(w => {
          if (props.restriction.project === 'alpha') {
            arrayWorkspaces.push(`workspace:${w}`);
          } else {
            arrayWorkspaces.push(w);
          }
        });
      }

      const arrayWtype = [];

      if (content_type.value) {
        if (props.restriction.project === 'alpha') {
          arrayWtype.push(`wtype:${content_type.value}`);
        } else {
          arrayWtype.push({ term: { wtype: content_type.value } });
        }
      }

      return [arrayWorkspaces, arrayWtype];
    });

    const variables = computed(() => {
      if (props.restriction.project === 'alpha') {
        return {
          query: search.value,
          args: {
            page: 0,
            hitsPerPage: 10,
            enableRules: false,
            attributesToRetrieve: ['wtitle', 'uuid', 'workspace', 'wtype', 'center_variation_season', 'center_variation_audience'],
            filters: 'NOT wtype:folder',
            facetFilters: facetFilters.value
          }
        };
      }

      return {
        input: {
          query: {
            bool: {
              must: [
                {
                  simple_query_string: {
                    query: `${search.value || ''}*`,
                    fields: ['wtitle', 'uuid*', 'url*', 'iresaPackageCode']
                  }
                },
                facetFilters.value[1][0]
              ],
              // must_not: { term: { wtype: 'folder' } },
              filter: [
                { terms: { workspace: facetFilters.value[0] } }
              ]
            }
          }
        }
      };
    });

    const filename = computed(() => (props.restriction.project === 'alpha' ? 'searchAlgolia' : 'speedboatSearchOpenSearch'));

    const { result, loading: loadingContents, onResult } = useQuery(getGraphqlSchema(filename.value), variables, () => ({
      enabled: enableQuery,
      clientId: client,
      fetchPolicy: 'cache-first'
    }));

    onResult(r => {
      if (!r.loading) {
        resultCount.value = r.data.search.count;
        if (r.data.search.items.length === 0) {
          if (!search.value) {
            errors.value = [getLabel('no_contents_error')];
            emit('showErrors', errors.value);
          }
        }
      }
    });

    /** ********************************************** */

    // First Request to get the data of contents already saved

    // true after getting the data of the contents already in the restriction
    const firstRequest = ref(true);

    const uuidFacets = computed(() => props.restriction.settings.limited_contents || []);

    const enablePreContentsQuery = computed(() => {
      const isEmpty = !props.restriction.settings.limited_contents;
      if (props.stepper === 3 && firstRequest.value && !isEmpty) {
        return true;
      }
      return false;
    });

    const preRequestVariables = computed(() => {
      if (props.restriction.project === 'alpha') {
        return {
          query: '',
          args: {
            page: 0,
            enableRules: false,
            attributesToRetrieve: ['wtitle', 'uuid', 'workspace', 'wtype'],
            filters: 'NOT wtype:folder',
            facetFilters: [uuidFacets.value.map(uuid => `uuid: ${uuid}`)]
          }
        };
      }
      return { input: { query: { ids: { values: uuidFacets.value } } } };
    });

    const { onResult: onResultPreContent, loading: preContentsLoading } = useQuery(
      getGraphqlSchema(filename.value),
      preRequestVariables,
      () => ({
        enabled: enablePreContentsQuery,
        clientId: client,
        fetchPolicy: 'network-only'
      })
    );

    onResultPreContent(r => {
      if (!r.loading) {
        firstRequest.value = false;
        limited_contentsInfo.value = r.data.search.items;
      }
    });

    /** ************************************************** */

    const contents = computed(() => (loadingContents.value ? [] : result.value?.search.items));

    const previousStep = () => {
      limited_contentsInfo.value = [];
      selectedLimitedContent.value = [];
      settings.limited_contents = [];

      emit('previousStep', { ...props.restriction, settings: {} });
    };

    const nextStep = () => {
      // When there isn't any content selected
      if (content_type === null || settings.limited_contents.length === 0) {
        errors.value = [getLabel('insert_minimum_content')];
        emit('showErrors', errors.value);
        return;
      }

      settings.content_type = content_type.value;

      emit('updateRestriction', { ...props.restriction, settings: settings });
    };

    const autocompleteWasFocus = () => {
      triggerSearch.value = true;
    };

    const onChange = () => {
      // to clean the search and close the dropbox
      search.value = '';
      refs.contentSelect.blur();

      // if already in limited_contents
      if (settings.limited_contents.filter(c => c === selectedLimitedContent.value[0].uuid).length > 0) {
        selectedLimitedContent.value = [];
        return;
      }
      limited_contentsInfo.value = limited_contentsInfo.value.concat(selectedLimitedContent.value[0]);

      settings.limited_contents.push(selectedLimitedContent.value[0].uuid);

      selectedLimitedContent.value = [];
    };

    const removeLimitedContent = content => {
      settings.limited_contents = settings.limited_contents.filter(c => c !== content.uuid);

      limited_contentsInfo.value = limited_contentsInfo.value.filter(c => c.uuid !== content.uuid);
    };

    const onChangeContentType = () => {
      limited_contentsInfo.value = [];
      settings.limited_contents = [];
    };

    const getSubTitle = (item, workspace = false) => {
      let subTitle = '';

      switch (item.wtype) {
        case 'center_variation':
          subTitle += `${getContentTypeLabel(item.wtype)} - ${item.center_variation_season ? item.center_variation_season : ' '} - ${item.center_variation_audience ? item.center_variation_audience : ' '}`;
          break;
        default:
          subTitle += `${getContentTypeLabel(item.wtype)} (${getWorkspaceLabel(item.workspace)})`;
      }

      return subTitle + (workspace ? ` (${getWorkspaceLabel(item.workspace)})` : '');
    };

    return {
      getLabel,
      getWorkspaceLabel,
      getContentTypeLabel,
      search,
      errors,
      contentTypesToShow,
      content_type,
      selectedLimitedContent,
      limited_contentsInfo,
      isError,
      resultCount,
      loadingContents,
      preContentsLoading,
      contents,
      previousStep,
      nextStep,
      autocompleteWasFocus,
      onChange,
      onChangeContentType,
      removeLimitedContent,
      getSubTitle
    };
  }
};

</script>
